import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const RESULT = {
  connected: ["success", "Outlook connected"],
  cancelled: ["info", "Outlook sign-in cancelled"],
  expired: ["error", "That sign-in link expired — try again"],
  failed: ["error", "Couldn't finish connecting Outlook — try again"],
};

// Admin's Outlook connection status, shared by the events pages.
export const useOutlook = () => {
  const [status, setStatus] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/outlook/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  return { status, reload: load };
};

const connect = async () => {
  try {
    const { data } = await api.post("/admin/outlook/start");
    window.location.href = data.url;
  } catch (err) {
    toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't start Outlook sign-in.");
  }
};

export const OutlookCard = () => {
  const { status, reload } = useOutlook();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const res = RESULT[params.get("outlook")];
    if (!res) return;
    toast[res[0]](res[1]);
    params.delete("outlook");
    setParams(params, { replace: true });
  }, [params, setParams]);

  if (!status) return null;
  const disconnect = async () => {
    if (!window.confirm("Disconnect Outlook?")) return;
    setBusy(true);
    await api.post("/admin/outlook/disconnect").finally(() => setBusy(false));
    reload();
  };

  return (
    <div data-testid="outlook-card" className="mt-4 rounded-2xl bg-white border border-black/10 px-4 py-3">
      <div className="flex items-center gap-3">
        <Mail className="h-5 w-5 text-[#0a64ad] shrink-0" />
        <div className="min-w-0 flex-1 text-[12.5px]">
          <b className="text-brand-charcoal">Outlook</b>
          <span className="block text-brand-charcoal/60 truncate" data-testid="outlook-status">
            {!status.configured ? "Not set up yet" : status.connected ? `Connected: ${status.account_email}` : "Not connected"}
          </span>
        </div>
        {status.configured && (
          <button type="button" onClick={connect} data-testid="outlook-connect" className="h-9 px-3.5 rounded-full bg-[#0a64ad] text-white text-[10.5px] font-bold uppercase tracking-[0.08em] shrink-0">
            {status.connected ? "Switch account" : "Connect"}
          </button>
        )}
        {status.connected && (
          <button type="button" onClick={disconnect} disabled={busy} data-testid="outlook-disconnect" className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-charcoal/50 shrink-0">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Disconnect"}
          </button>
        )}
      </div>
      {!status.configured && (
        <p className="mt-2 text-[11px] text-brand-charcoal/55 break-all" data-testid="outlook-redirect-uri">
          Needs a Microsoft app registration. Redirect URI to register: {status.redirect_uri}
        </p>
      )}
    </div>
  );
};

export default OutlookCard;
